import { polygonVertexLocation } from "@constructionautomation/backend/convex/lib/measurementGeometry";
import { notifyError } from "@/lib/notify-error";
import {
  type ComponentProps,
  type ReactNode,
  useEffect,
  useState,
} from "react";

import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ViewportContextMenu,
  ViewportContextMenuItem,
} from "@/components/ui/viewport-context-menu";
import { ViewportSelectionPanel as MeasurementSelectionCard } from "@/components/ui/viewport-selection-panel";
import { useDocumentViewport } from "@/features/document-viewport/context";
import { AddCustomLineItemDialog } from "@/features/takeoff-pilot/add-custom-line-item-dialog";
import type { MeasurementScale } from "@/hooks/useMeasurementScale";
import { formatMeasurementHeight, type Point } from "@/lib/geometry";
import { getUserFacingErrorMessage } from "@/lib/user-facing-errors";
import { TOOL_IDS } from "@/viewport-tools/measurement";
import { Check, CopyPlus, Flag, Group, Minus, Plus, Trash2, Ungroup, X } from "lucide-react";
import { toast } from "sonner";
import {
  canGroupSelectedMeasurements,
  selectionLabelGroupState,
} from "@/viewport-tools/measurement/overlay/measurement-label-groups";
import {
  applyMeasurementLeaderVisibility,
  formatDisplayedMeasurement,
  getEditableValueLabel,
  getScreenBoundsForGeometry,
  measurementRequiresHeight,
  shouldShowMeasurementLeader,
} from "@/viewport-tools/measurement/overlay/measurement-overlay-helpers";
import type {
  DraftMeasurementState,
  MeasurementEditDraft,
  MeasurementAnnotation,
  MeasurementOverlayRecord,
  MeasurementTagOption,
  PolygonGeometry,
} from "@/viewport-tools/measurement/overlay/measurement-overlay-types";
import { MEASUREMENT_GEOMETRY_TYPES } from "@/viewport-tools/measurement/overlay/measurement-overlay-types";

const UNASSIGNED_TAG_VALUE = "__unassigned_tag__";
const ADD_CUSTOM_LINE_ITEM_VALUE = "__add_custom_line_item__";

function isPointEditableMeasurement(
  measurement: MeasurementOverlayRecord | null
): measurement is MeasurementOverlayRecord & { geometry: PolygonGeometry } {
  return (
    !!measurement &&
    measurement.geometry.type === MEASUREMENT_GEOMETRY_TYPES.poly &&
    (measurement.tool === TOOL_IDS.polyline || measurement.tool === TOOL_IDS.polygon)
  );
}

function canInsertPointOnMeasurement(measurement: MeasurementOverlayRecord | null) {
  return isPointEditableMeasurement(measurement) && measurement.geometry.vertices.length >= 2;
}

function canRemovePointFromMeasurement(measurement: MeasurementOverlayRecord | null, vertexIndex: number) {
  if (!isPointEditableMeasurement(measurement)) {
    return false;
  }
  const minVertexCount = measurement.tool === TOOL_IDS.polygon ? 3 : 2;
  return (polygonVertexLocation(measurement.geometry, vertexIndex)?.ring.length ?? 0) > minVertexCount;
}

function getContextSelectionIds(
  measurement: MeasurementOverlayRecord | null,
  selectedMeasurementIds: Id<"takeoffPilotMeasurements">[]
) {
  if (!measurement) {
    return [];
  }
  return selectedMeasurementIds.includes(measurement._id)
    ? selectedMeasurementIds
    : [measurement._id];
}

export function DraftMeasurementCard({
  draft,
  draftTagId,
  tags,
  onDraftTagChange,
  onCreateCustomLineItem,
  onSaveDraftMeasurement,
  onCancelDraftMeasurement,
  isSavingDraftMeasurement,
}: {
  draft: DraftMeasurementState;
  draftTagId: Id<"takeoffPilotExtractedTags"> | null;
  tags: MeasurementTagOption[];
  onDraftTagChange: (tagId: Id<"takeoffPilotExtractedTags">) => void;
  onCreateCustomLineItem?: (args: {
    rawValue: string;
    unitCost: number | null;
  }) => Promise<Id<"takeoffPilotExtractedTags"> | void>;
  onSaveDraftMeasurement: () => Promise<void>;
  onCancelDraftMeasurement: () => void;
  isSavingDraftMeasurement: boolean;
}) {
  const viewport = useDocumentViewport();
  const bounds = getScreenBoundsForGeometry(draft.geometry, viewport);
  const cardWidth = 144;
  const cardLeft = Math.min(
    bounds.x + bounds.width + 10,
    viewport.workspaceSize.width - cardWidth - 12
  );
  const cardHeight = measurementRequiresHeight(draft.tool, draft.measurementType) ? 172 : 142;
  const maxCardTop = Math.max(12, viewport.workspaceSize.height - cardHeight - 12);
  const [isAddCustomDialogOpen, setIsAddCustomDialogOpen] = useState(false);
  const cardTop = Math.min(Math.max(12, bounds.y), maxCardTop);

  return (
    <div
      className="absolute z-40 pointer-events-auto w-36 rounded-md border bg-background/95 p-2 shadow-xl backdrop-blur"
      style={{ left: cardLeft, top: cardTop }}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
        {draft.tool} - {draft.measurementType}
      </div>
      <div className="mb-2 text-center">
        <span className="text-lg font-black">
          {formatDisplayedMeasurement(draft.measurementType, draft.value)}
        </span>
        <span className="ms-1 text-sm text-muted-foreground">{draft.measurementType}</span>
      </div>
      {measurementRequiresHeight(draft.tool, draft.measurementType) && (
        <div className="mb-2 text-xs text-muted-foreground">
          Height: {draft.height ? formatMeasurementHeight(draft.height) : "Not set"}
        </div>
      )}
      <Select
        value={draftTagId ? String(draftTagId) : undefined}
        onValueChange={(value) => {
          if (value === ADD_CUSTOM_LINE_ITEM_VALUE) {
            setIsAddCustomDialogOpen(true);
            return;
          }
          onDraftTagChange(value as Id<"takeoffPilotExtractedTags">);
        }}
      >
        <SelectTrigger className="mb-2 h-7 w-full bg-background px-2 text-xs">
          <SelectValue placeholder="Assign tag" />
        </SelectTrigger>
        <SelectContent
          className="z-50 max-h-64"
          position="popper"
          sideOffset={4}
          align="start"
          collisionPadding={8}
        >
          {tags.map((tag) => (
            <SelectItem key={tag.id} value={String(tag.id)}>
              {tag.label}
            </SelectItem>
          ))}
          {onCreateCustomLineItem ? (
            <SelectItem value={ADD_CUSTOM_LINE_ITEM_VALUE}>+ Custom</SelectItem>
          ) : null}
        </SelectContent>
      </Select>
      {onCreateCustomLineItem ? (
        <AddCustomLineItemDialog
          open={isAddCustomDialogOpen}
          onOpenChange={setIsAddCustomDialogOpen}
          onCreateCustomLineItem={async (args) => {
            const tagId = await onCreateCustomLineItem(args);
            if (tagId) {
              onDraftTagChange(tagId);
            }
          }}
        />
      ) : null}
      <div className="flex justify-end gap-1">
        <IconButton
          label="Cancel draft measurement"
          icon={X}
          size="compact"
          onClick={onCancelDraftMeasurement}
        />
        <IconButton
          label="Save draft measurement"
          icon={Check}
          size="compact"
          variant="default"
          disabled={!draftTagId || isSavingDraftMeasurement}
          onClick={() => void onSaveDraftMeasurement()}
        />
      </div>
    </div>
  );
}

// Icon-only action button with a delayed hover tooltip, wrapped in a span so the
// tooltip still appears while the button is disabled.
function MeasurementActionButton({
  label,
  tooltip,
  icon,
  onClick,
  disabled,
  variant = "ghost",
}: {
  label: string;
  tooltip?: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  variant?: ComponentProps<typeof Button>["variant"];
}) {
  return (
    <Tooltip delayDuration={700}>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <Button
            type="button"
            variant={variant}
            size="icon-sm"
            disabled={disabled}
            aria-label={label}
            onClick={(event) => {
              event.stopPropagation();
              onClick();
            }}
          >
            {icon}
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltip ?? label}</TooltipContent>
    </Tooltip>
  );
}

// Editor shown when several measurements are selected at once. Applies tag and
// leader changes to the whole selection immediately.
function MultiMeasurementCard({
  selectedMeasurements,
  tags,
  onReassignMeasurements,
  onApplyMeasurementLeaders,
  onUpdateMeasurementAnnotation,
  onDeleteMeasurements,
  onDuplicateMeasurements,
  onGroupLabels,
  onUngroupLabels,
  onGroupAllLabelsInScope,
  onUngroupAllLabelsInScope,
  onClose,
}: {
  selectedMeasurements: MeasurementOverlayRecord[];
  tags: MeasurementTagOption[];
  onReassignMeasurements?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    tagId: Id<"takeoffPilotExtractedTags">
  ) => Promise<boolean>;
  onApplyMeasurementLeaders?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    leaderVisible: boolean
  ) => Promise<boolean>;
  onUpdateMeasurementAnnotation?: (
    measurementId: Id<"takeoffPilotMeasurements">,
    annotation: MeasurementAnnotation
  ) => Promise<void>;
  onDeleteMeasurements?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<void>;
  onDuplicateMeasurements?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<void> | void;
  onGroupLabels?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<boolean>;
  onUngroupLabels?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<boolean>;
  onGroupAllLabelsInScope?: () => Promise<boolean>;
  onUngroupAllLabelsInScope?: () => Promise<boolean>;
  onClose?: () => void;
}) {
  const [tagDraftId, setTagDraftId] = useState<Id<"takeoffPilotExtractedTags"> | null>(null);
  const [leaderVisibleDraft, setLeaderVisibleDraft] = useState<boolean | "indeterminate">(false);
  const [isSavingEdits, setIsSavingEdits] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const canApplyLeaderUpdates = !!onApplyMeasurementLeaders || !!onUpdateMeasurementAnnotation;

  useEffect(() => {
    const selectedLeadersCount = selectedMeasurements.filter((item) =>
      shouldShowMeasurementLeader(item.annotation)
    ).length;
    const hasMixedLeaders =
      selectedLeadersCount > 0 && selectedLeadersCount < selectedMeasurements.length;
    const allLeadersVisible = selectedLeadersCount === selectedMeasurements.length;
    setTagDraftId(null);
    // Mixed visibility uses indeterminate so one click can show all leaders.
    setLeaderVisibleDraft(hasMixedLeaders ? "indeterminate" : allLeadersVisible);
    setEditError(null);
  }, [selectedMeasurements]);

  const measurementIds = selectedMeasurements.map((item) => item._id);
  const groupEligibility = canGroupSelectedMeasurements(selectedMeasurements);
  const labelGroupState = selectionLabelGroupState(selectedMeasurements);
  const canGroupSelection = !!onGroupLabels && groupEligibility.ok;
  const canUngroupSelection = !!onUngroupLabels && labelGroupState.anyGrouped;

  // Reflect the current assignment of the selection: show the shared tag when
  // every measurement matches, otherwise flag that the tags differ.
  const uniqueTagIds = Array.from(
    new Set(
      selectedMeasurements.map((item) => (item.tagId ? String(item.tagId) : UNASSIGNED_TAG_VALUE))
    )
  );
  const hasMixedTags = uniqueTagIds.length > 1;
  const commonTagId = hasMixedTags ? null : (selectedMeasurements[0]?.tagId ?? null);
  const tagSelectValue = tagDraftId
    ? String(tagDraftId)
    : commonTagId
      ? String(commonTagId)
      : UNASSIGNED_TAG_VALUE;
  const tagPlaceholder = hasMixedTags ? "Multiple tags" : "Assign tag";
  const canRunScopeActions = !hasMixedTags && (!!onGroupAllLabelsInScope || !!onUngroupAllLabelsInScope);

  const handleTagChange = async (value: string) => {
    const nextTagId =
      value === UNASSIGNED_TAG_VALUE ? null : (value as Id<"takeoffPilotExtractedTags">);
    const previousTagId = tagDraftId;
    setTagDraftId(nextTagId);
    if (!nextTagId) {
      return;
    }
    // Tag applies to every selected measurement (the card edits the whole selection).
    try {
      setIsSavingEdits(true);
      setEditError(null);
      const reassigned = await onReassignMeasurements?.(measurementIds, nextTagId);
      if (reassigned === false) {
        throw new Error("Failed to update tag");
      }
      toast.success(`${measurementIds.length} measurements updated`);
    } catch (error) {
      setTagDraftId(previousTagId);
      setEditError(getUserFacingErrorMessage(error, "Failed to update tag"));
    } finally {
      setIsSavingEdits(false);
    }
  };

  const handleLeaderChange = async (checked: boolean) => {
    if (!canApplyLeaderUpdates) {
      return;
    }
    const previousLeaderVisible = leaderVisibleDraft;
    setLeaderVisibleDraft(checked);
    try {
      setIsSavingEdits(true);
      setEditError(null);
      await applyMeasurementLeaderVisibility(measurementIds, checked, {
        onApplyMeasurementLeaders,
        onUpdateMeasurementAnnotation,
      });
      toast.success(`${measurementIds.length} measurements updated`);
    } catch (error) {
      setLeaderVisibleDraft(previousLeaderVisible);
      setEditError(getUserFacingErrorMessage(error, "Failed to update measurement leaders"));
    } finally {
      setIsSavingEdits(false);
    }
  };

  const handleGroupLabels = async () => {
    if (!onGroupLabels) return;
    try {
      setIsSavingEdits(true);
      setEditError(null);
      const grouped = await onGroupLabels(measurementIds);
      if (grouped === false) {
        throw new Error("Failed to group labels");
      }
      toast.success("Labels grouped");
    } catch (error) {
      setEditError(getUserFacingErrorMessage(error, "Failed to group labels"));
    } finally {
      setIsSavingEdits(false);
    }
  };

  const handleUngroupLabels = async () => {
    if (!onUngroupLabels) return;
    try {
      setIsSavingEdits(true);
      setEditError(null);
      const ungrouped = await onUngroupLabels(measurementIds);
      if (ungrouped === false) {
        throw new Error("Failed to ungroup labels");
      }
      toast.success("Labels ungrouped");
    } catch (error) {
      setEditError(getUserFacingErrorMessage(error, "Failed to ungroup labels"));
    } finally {
      setIsSavingEdits(false);
    }
  };

  return (
    <MeasurementSelectionCard
      title="Measurements"
      subtitle={`${selectedMeasurements.length} selected`}
      onClose={onClose}
      closeLabel="Close measurement editor"
      actions={
        <>
          {onGroupLabels ? (
            <MeasurementActionButton
              label="Group labels"
              icon={<Group className="size-4" />}
              disabled={isSavingEdits || !canGroupSelection}
              onClick={() => void handleGroupLabels()}
            />
          ) : null}
          {onUngroupLabels ? (
            <MeasurementActionButton
              label="Ungroup labels"
              icon={<Ungroup className="size-4" />}
              disabled={isSavingEdits || !canUngroupSelection}
              onClick={() => void handleUngroupLabels()}
            />
          ) : null}
          <MeasurementActionButton
            label="Duplicate"
            icon={<CopyPlus className="size-4" />}
            disabled={isSavingEdits}
            onClick={() => void onDuplicateMeasurements?.(measurementIds)}
          />
          <MeasurementActionButton
            label="Delete"
            icon={<Trash2 className="size-4" />}
            variant="ghost"
            disabled={isSavingEdits}
            onClick={() => void onDeleteMeasurements?.(measurementIds)}
          />
        </>
      }
    >
      <div className="space-y-1">
        <Label className="text-xs">{hasMixedTags ? "Tag (multiple selected)" : "Tag (all)"}</Label>
        <Select value={tagSelectValue} onValueChange={handleTagChange} disabled={isSavingEdits}>
          <SelectTrigger className="h-8">
            <SelectValue placeholder={tagPlaceholder} />
          </SelectTrigger>
          <SelectContent
            className="z-[160] max-h-64"
            position="popper"
            sideOffset={4}
            align="start"
            collisionPadding={8}
          >
            <SelectItem value={UNASSIGNED_TAG_VALUE} disabled>
              {tagPlaceholder}
            </SelectItem>
            {tags.map((tag) => (
              <SelectItem key={tag.id} value={String(tag.id)}>
                {tag.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <label className="flex items-center gap-2 text-xs text-muted-foreground">
        <Checkbox
          aria-label="Show leader for selected measurements"
          checked={leaderVisibleDraft}
          onCheckedChange={(checked) => void handleLeaderChange(checked === true)}
          disabled={!canApplyLeaderUpdates || isSavingEdits}
        />
        Leader
      </label>

      {canRunScopeActions ? (
        <div className="flex flex-wrap gap-1">
          {onGroupAllLabelsInScope ? (
            <Button
              type="button"
              size="compact"
              variant="outline"
              disabled={isSavingEdits}
              onClick={() => {
                void (async () => {
                  try {
                    setIsSavingEdits(true);
                    setEditError(null);
                    const ok = await onGroupAllLabelsInScope();
                    if (ok === false) throw new Error("Failed to group all labels");
                    toast.success("Grouped all labels in scope");
                  } catch (error) {
                    setEditError(getUserFacingErrorMessage(error, "Failed to group all labels"));
                  } finally {
                    setIsSavingEdits(false);
                  }
                })();
              }}
            >
              Group all in tag
            </Button>
          ) : null}
          {onUngroupAllLabelsInScope ? (
            <Button
              type="button"
              size="compact"
              variant="outline"
              disabled={isSavingEdits}
              onClick={() => {
                void (async () => {
                  try {
                    setIsSavingEdits(true);
                    setEditError(null);
                    const ok = await onUngroupAllLabelsInScope();
                    if (ok === false) throw new Error("Failed to ungroup all labels");
                    toast.success("Ungrouped all labels in scope");
                  } catch (error) {
                    setEditError(getUserFacingErrorMessage(error, "Failed to ungroup all labels"));
                  } finally {
                    setIsSavingEdits(false);
                  }
                })();
              }}
            >
              Ungroup all in tag
            </Button>
          ) : null}
        </div>
      ) : null}

      {!groupEligibility.ok && onGroupLabels ? (
        <div className="text-[11px] leading-tight text-muted-foreground">{groupEligibility.reason}</div>
      ) : null}

      {editError ? (
        <div className="text-[11px] leading-tight text-destructive">{editError}</div>
      ) : null}
    </MeasurementSelectionCard>
  );
}

export function SelectedMeasurementCard({
  measurement,
  selectedMeasurements,
  tags,
  scale,
  onSaveMeasurementEdits,
  onUpdateMeasurementAnnotation,
  onApplyMeasurementLeaders,
  onReassignMeasurement,
  onReassignMeasurements,
  isReassigningMeasurementId,
  isDeletingMeasurementId,
  onDeleteMeasurement,
  onDeleteMeasurements,
  onDuplicateMeasurements,
  onGroupLabels,
  onUngroupLabels,
  onGroupAllLabelsInScope,
  onUngroupAllLabelsInScope,
  onClose,
}: {
  measurement: MeasurementOverlayRecord | null;
  selectedMeasurements: MeasurementOverlayRecord[];
  tags: MeasurementTagOption[];
  scale: MeasurementScale | null;
  onSaveMeasurementEdits: (
    measurementId: Id<"takeoffPilotMeasurements">,
    edits: MeasurementEditDraft
  ) => Promise<void>;
  onUpdateMeasurementAnnotation?: (
    measurementId: Id<"takeoffPilotMeasurements">,
    annotation: MeasurementAnnotation
  ) => Promise<void>;
  onApplyMeasurementLeaders?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    leaderVisible: boolean
  ) => Promise<boolean>;
  onReassignMeasurement: (
    measurementId: Id<"takeoffPilotMeasurements">,
    tagId: Id<"takeoffPilotExtractedTags">
  ) => Promise<boolean>;
  onReassignMeasurements?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    tagId: Id<"takeoffPilotExtractedTags">
  ) => Promise<boolean>;
  onGroupLabels?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<boolean>;
  onUngroupLabels?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<boolean>;
  onGroupAllLabelsInScope?: () => Promise<boolean>;
  onUngroupAllLabelsInScope?: () => Promise<boolean>;
  isReassigningMeasurementId: Id<"takeoffPilotMeasurements"> | null;
  isDeletingMeasurementId: Id<"takeoffPilotMeasurements"> | null;
  onDeleteMeasurement: (measurementId: Id<"takeoffPilotMeasurements">) => Promise<void>;
  onDeleteMeasurements?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<void>;
  onDuplicateMeasurements?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<void> | void;
  onClose?: () => void;
}) {
  const [tagDraftId, setTagDraftId] = useState<Id<"takeoffPilotExtractedTags"> | null>(
    () => measurement?.tagId ?? null
  );
  const [valueDraft, setValueDraft] = useState<string>("");
  const [heightDraft, setHeightDraft] = useState<string>("");
  const [leaderVisibleDraft, setLeaderVisibleDraft] = useState(false);
  const [strokeWidthDraft, setStrokeWidthDraft] = useState<string>("");
  const [editError, setEditError] = useState<string | null>(null);
  const [isSavingEdits, setIsSavingEdits] = useState(false);

  useEffect(() => {
    setTagDraftId(measurement?.tagId ?? null);
    setValueDraft(
      measurement
        ? formatDisplayedMeasurement(measurement.measurementType, measurement.value)
        : ""
    );
    setHeightDraft(measurement?.height ? formatMeasurementHeight(measurement.height) : "");
    setLeaderVisibleDraft(shouldShowMeasurementLeader(measurement?.annotation));
    setStrokeWidthDraft(measurement?.annotation?.strokeWidth?.toString() ?? "");
    setEditError(null);
  }, [measurement, selectedMeasurements]);

  if (!measurement) {
    return null;
  }

  if (selectedMeasurements.length > 1) {
    return (
      <MultiMeasurementCard
        selectedMeasurements={selectedMeasurements}
        tags={tags}
        onReassignMeasurements={onReassignMeasurements}
        onApplyMeasurementLeaders={onApplyMeasurementLeaders}
        onUpdateMeasurementAnnotation={onUpdateMeasurementAnnotation}
        onDeleteMeasurements={onDeleteMeasurements}
        onDuplicateMeasurements={onDuplicateMeasurements}
        onGroupLabels={onGroupLabels}
        onUngroupLabels={onUngroupLabels}
        onGroupAllLabelsInScope={onGroupAllLabelsInScope}
        onUngroupAllLabelsInScope={onUngroupAllLabelsInScope}
        onClose={onClose}
      />
    );
  }

  const requiresHeight = measurementRequiresHeight(
    measurement.tool,
    measurement.measurementType
  );
  const currentValueText = formatDisplayedMeasurement(
    measurement.measurementType,
    measurement.value
  );
  const currentHeightText = measurement.height
    ? formatMeasurementHeight(measurement.height)
    : "";
  const currentStrokeWidth = measurement.annotation?.strokeWidth;
  const valueChanged = valueDraft.trim() !== currentValueText;
  const heightChanged = requiresHeight && heightDraft.trim() !== currentHeightText;
  const tagChanged = tagDraftId !== (measurement.tagId ?? null);
  const leaderChanged = leaderVisibleDraft !== shouldShowMeasurementLeader(measurement.annotation);
  const strokeWidthChanged = strokeWidthDraft !== (currentStrokeWidth?.toString() ?? "");
  const requiresScaleForSave = valueChanged || heightChanged;
  const hasValidScale = (scale?.pixelsPerFoot ?? 0) > 0;
  const hasChanges = valueChanged || heightChanged || tagChanged || leaderChanged || strokeWidthChanged;
  const valueLabel = getEditableValueLabel(measurement.measurementType);

  const stepStrokeWidth = (delta: number) => {
    const parsedDraft = parseFloat(strokeWidthDraft);
    const base = Number.isFinite(parsedDraft) ? parsedDraft : (currentStrokeWidth ?? 2);
    const next = Math.max(0.5, Math.round((base + delta) * 2) / 2);
    setStrokeWidthDraft(String(next));
    if (editError) {
      setEditError(null);
    }
  };

  const saveChanges = async () => {
    try {
      setIsSavingEdits(true);
      setEditError(null);
      if (valueChanged || heightChanged) {
        await onSaveMeasurementEdits(measurement._id, {
          valueInput: valueDraft,
          valueChanged,
          heightInput: heightDraft,
          heightChanged,
        });
      }
      if (tagChanged && tagDraftId) {
        const reassigned = await onReassignMeasurement(measurement._id, tagDraftId);
        if (!reassigned) {
          throw new Error("Failed to update tag");
        }
      }
      if (leaderChanged) {
        await applyMeasurementLeaderVisibility([measurement._id], leaderVisibleDraft, {
          onApplyMeasurementLeaders,
          onUpdateMeasurementAnnotation,
        });
      }
      if (strokeWidthChanged) {
        const trimmedStrokeWidth = strokeWidthDraft.trim();
        const annotationUpdates: MeasurementAnnotation = {};
        if (trimmedStrokeWidth.length === 0) {
          annotationUpdates.strokeWidth = undefined;
        } else {
          const parsedStrokeWidth = parseFloat(trimmedStrokeWidth);
          if (!Number.isFinite(parsedStrokeWidth) || parsedStrokeWidth <= 0) {
            setEditError("Stroke width must be a positive number");
            return;
          }
          annotationUpdates.strokeWidth = parsedStrokeWidth;
        }
        await onUpdateMeasurementAnnotation?.(measurement._id, annotationUpdates);
      }
      toast.success("Measurement updated");
    } catch (error) {
      setEditError(getUserFacingErrorMessage(error, "Failed to update measurement"));
    } finally {
      setIsSavingEdits(false);
    }
  };

  const saveDisabled =
    (requiresScaleForSave && !hasValidScale) ||
    !hasChanges ||
    isSavingEdits ||
    isReassigningMeasurementId === measurement._id ||
    isDeletingMeasurementId === measurement._id;

  return (
    <MeasurementSelectionCard
      title="Measurement"
      subtitle={`${measurement.tool.charAt(0).toUpperCase()}${measurement.tool.slice(1)} · ${measurement.measurementType}`}
      onClose={onClose}
      closeLabel="Close measurement editor"
      onKeyDown={(event) => {
        if (event.key !== "Enter") {
          return;
        }
        const target = event.target as HTMLElement | null;
        const tagName = target?.tagName ?? "";
        const role = target?.getAttribute("role");
        if (tagName !== "INPUT" || role === "combobox") {
          return;
        }
        if (saveDisabled) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        void saveChanges();
      }}
      actions={
        <>
          <MeasurementActionButton
            label="Duplicate"
            icon={<CopyPlus className="size-4" />}
            disabled={
              isSavingEdits ||
              isDeletingMeasurementId === measurement._id ||
              isReassigningMeasurementId === measurement._id
            }
            onClick={() => void onDuplicateMeasurements?.([measurement._id])}
          />
          <MeasurementActionButton
            label="Delete measurement"
            tooltip="Delete"
            icon={<Trash2 className="size-4" />}
            disabled={
              isDeletingMeasurementId === measurement._id ||
              isSavingEdits ||
              isReassigningMeasurementId === measurement._id
            }
            onClick={() => void onDeleteMeasurement(measurement._id)}
          />
          <MeasurementActionButton
            label="Save measurement changes"
            tooltip="Save"
            variant="secondary"
            icon={<Check className="size-4" />}
            disabled={saveDisabled}
            onClick={() => void saveChanges()}
          />
        </>
      }
    >
      <div className="space-y-1">
        <Label className="text-xs">{valueLabel}</Label>
        <Input
          aria-label="Measurement value"
          value={valueDraft}
          onChange={(event) => {
            setValueDraft(event.target.value);
            if (editError) {
              setEditError(null);
            }
          }}
          disabled={(requiresScaleForSave && !hasValidScale) || isSavingEdits}
          className="h-8"
        />
      </div>
      {requiresHeight ? (
        <div className="space-y-1">
          <Label className="text-xs">Height</Label>
          <Input
            aria-label="Measurement height"
            value={heightDraft}
            onChange={(event) => {
              setHeightDraft(event.target.value);
              if (editError) {
                setEditError(null);
              }
            }}
            disabled={isSavingEdits}
            className="h-8"
          />
        </div>
      ) : null}
      <div className="space-y-1">
        <Label className="text-xs">Tag</Label>
        <Select
          value={tagDraftId ? String(tagDraftId) : UNASSIGNED_TAG_VALUE}
          onValueChange={(value) =>
            setTagDraftId(
              value === UNASSIGNED_TAG_VALUE
                ? null
                : (value as Id<"takeoffPilotExtractedTags">)
            )
          }
        >
          <SelectTrigger className="h-8">
            <SelectValue placeholder="Assign tag" />
          </SelectTrigger>
          <SelectContent
            className="z-[160] max-h-64"
            position="popper"
            sideOffset={4}
            align="start"
            collisionPadding={8}
          >
            <SelectItem value={UNASSIGNED_TAG_VALUE} disabled>
              Assign tag
            </SelectItem>
            {tags.map((tag) => (
              <SelectItem key={tag.id} value={String(tag.id)}>
                {tag.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Checkbox
            aria-label="Show measurement leader"
            checked={leaderVisibleDraft}
            onCheckedChange={(checked) => setLeaderVisibleDraft(checked === true)}
          />
          Leader
        </label>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span>Stroke</span>
          <Input
            aria-label="Stroke width"
            type="number"
            min={0.5}
            step={0.5}
            value={strokeWidthDraft}
            onChange={(event) => {
              setStrokeWidthDraft(event.target.value);
              if (editError) {
                setEditError(null);
              }
            }}
            disabled={isSavingEdits}
            className="h-7 w-11 px-1.5 text-center text-xs [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            placeholder="2"
          />
          <div className="flex flex-col">
            <IconButton
              label="Increase stroke width"
              icon={Plus}
              variant="outline"
              size="xs"
              tooltipSide="right"
              className="h-3.5 w-5 rounded-b-none"
              iconClassName="size-2.5"
              disabled={isSavingEdits}
              onClick={() => stepStrokeWidth(0.5)}
            />
            <IconButton
              label="Decrease stroke width"
              icon={Minus}
              variant="outline"
              size="xs"
              tooltipSide="right"
              className="h-3.5 w-5 rounded-t-none border-t-0"
              iconClassName="size-2.5"
              disabled={isSavingEdits}
              onClick={() => stepStrokeWidth(-0.5)}
            />
          </div>
        </div>
      </div>
      {editError ? (
        <div className="text-[11px] leading-tight text-destructive">{editError}</div>
      ) : null}
    </MeasurementSelectionCard>
  );
}

/**
 * "Milestones" action for a single measurement. Extracted so its guard conditions live outside
 * `MeasurementContextMenu` (which is already at the complexity limit). Renders nothing when the
 * action isn't available or more than one measurement is in the context selection.
 */
function MilestonesContextMenuItem({
  measurement,
  contextSelectionCount,
  onOpenMilestones,
  onClose,
}: {
  measurement: MeasurementOverlayRecord | null;
  contextSelectionCount: number;
  onOpenMilestones?: (measurementId: Id<"takeoffPilotMeasurements">) => void;
  onClose: () => void;
}) {
  if (!measurement || !onOpenMilestones || contextSelectionCount > 1) {
    return null;
  }
  return (
    <ViewportContextMenuItem
      icon={<Flag className="size-3.5" />}
      onSelect={() => {
        onOpenMilestones(measurement._id);
        onClose();
      }}
    >
      Milestones
    </ViewportContextMenuItem>
  );
}

function LabelGroupContextMenuItems({
  canGroupSelection,
  canUngroupSelection,
  selectionIds,
  onGroupLabels,
  onUngroupLabels,
  onClose,
}: {
  canGroupSelection: boolean;
  canUngroupSelection: boolean;
  selectionIds: Id<"takeoffPilotMeasurements">[];
  onGroupLabels?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<boolean>;
  onUngroupLabels?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<boolean>;
  onClose: () => void;
}) {
  if (!canGroupSelection && !canUngroupSelection) {
    return null;
  }

  const runAction = async (
    action: ((measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<boolean>) | undefined,
    successMessage: string,
    failureMessage: string
  ) => {
    onClose();
    try {
      const ok = await action?.(selectionIds);
      if (ok === false) {
        toast.error(failureMessage);
        return;
      }
      toast.success(successMessage);
    } catch (error) {
      console.error(error);
      notifyError(error, getUserFacingErrorMessage(error, failureMessage));
    }
  };

  return (
    <>
      {canGroupSelection ? (
        <ViewportContextMenuItem
          onSelect={() => void runAction(onGroupLabels, "Labels grouped", "Failed to group labels")}
        >
          Group Labels
        </ViewportContextMenuItem>
      ) : null}
      {canUngroupSelection ? (
        <ViewportContextMenuItem
          onSelect={() =>
            void runAction(onUngroupLabels, "Labels ungrouped", "Failed to ungroup labels")
          }
        >
          Ungroup Labels
        </ViewportContextMenuItem>
      ) : null}
    </>
  );
}

export function MeasurementContextMenu({
  x,
  y,
  measurement,
  pagePoint,
  vertexIndex,
  measurements,
  canPasteMeasurement,
  selectedMeasurementIds,
  onClose,
  onCopyMeasurement,
  onCopyMeasurements,
  onPasteMeasurement,
  onDuplicateMeasurement,
  onDuplicateMeasurements,
  onDeleteMeasurement,
  onDeleteMeasurements,
  onApplyMeasurementLeaders,
  onGroupLabels,
  onUngroupLabels,
  onOpenMilestones,
  onInsertPoint,
  onRemovePoint,
}: {
  x: number;
  y: number;
  measurement: MeasurementOverlayRecord | null;
  pagePoint: Point | null;
  vertexIndex?: number;
  measurements: MeasurementOverlayRecord[];
  canPasteMeasurement: boolean;
  selectedMeasurementIds: Id<"takeoffPilotMeasurements">[];
  onClose: () => void;
  onCopyMeasurement?: (measurementId: Id<"takeoffPilotMeasurements">) => void;
  onCopyMeasurements?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => void;
  onPasteMeasurement?: () => Promise<void> | void;
  onDuplicateMeasurement?: (measurementId: Id<"takeoffPilotMeasurements">) => Promise<void> | void;
  onDuplicateMeasurements?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<void> | void;
  onDeleteMeasurement?: (measurementId: Id<"takeoffPilotMeasurements">) => Promise<void> | void;
  onDeleteMeasurements?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<void> | void;
  onApplyMeasurementLeaders?: (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    leaderVisible: boolean
  ) => Promise<boolean>;
  onGroupLabels?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<boolean>;
  onUngroupLabels?: (measurementIds: Id<"takeoffPilotMeasurements">[]) => Promise<boolean>;
  onOpenMilestones?: (measurementId: Id<"takeoffPilotMeasurements">) => void;
  onInsertPoint?: (
    measurementId: Id<"takeoffPilotMeasurements">,
    point: Point
  ) => Promise<void> | void;
  onRemovePoint?: (
    measurementId: Id<"takeoffPilotMeasurements">,
    vertexIndex: number
  ) => Promise<void> | void;
}) {
  if (!measurement && (!canPasteMeasurement || !onPasteMeasurement)) {
    return null;
  }

  const contextSelectionIds = getContextSelectionIds(measurement, selectedMeasurementIds);
  const selectedIdSet = new Set(contextSelectionIds.map((measurementId) => String(measurementId)));
  const selectedMeasurements = measurements.filter((item) => selectedIdSet.has(String(item._id)));
  const allLeadersVisible =
    selectedMeasurements.length > 0 &&
    selectedMeasurements.every((item) => shouldShowMeasurementLeader(item.annotation));
  const nextLeaderVisible = !allLeadersVisible;
  const leaderActionLabel =
    contextSelectionIds.length > 1
      ? nextLeaderVisible
        ? "Show Leaders for Selected"
        : "Hide Leaders for Selected"
      : nextLeaderVisible
        ? "Show Leader"
        : "Hide Leader";
  const groupEligibility = canGroupSelectedMeasurements(selectedMeasurements);
  const labelGroupState = selectionLabelGroupState(selectedMeasurements);
  const canGroupSelection = !!onGroupLabels && groupEligibility.ok;
  const canUngroupSelection = !!onUngroupLabels && labelGroupState.anyGrouped;
  const canInsertPoint =
    !!onInsertPoint &&
    !!pagePoint &&
    canInsertPointOnMeasurement(measurement);
  const canRemovePoint =
    !!onRemovePoint &&
    vertexIndex !== undefined &&
    canRemovePointFromMeasurement(measurement, vertexIndex);

  return (
    <ViewportContextMenu
      data-measurement-context-menu="true"
      x={x}
      y={y}
      onClose={onClose}
    >
      {measurement && onCopyMeasurement ? (
        <ViewportContextMenuItem
          onSelect={() => {
            onCopyMeasurement(measurement._id);
            onClose();
          }}
        >
          Copy
        </ViewportContextMenuItem>
      ) : null}
      {measurement && onCopyMeasurements && contextSelectionIds.length > 1 ? (
        <ViewportContextMenuItem
          onSelect={() => {
            onCopyMeasurements(contextSelectionIds);
            onClose();
          }}
        >
          Copy Selected
        </ViewportContextMenuItem>
      ) : null}
      {measurement && onDuplicateMeasurement ? (
        <ViewportContextMenuItem
          onSelect={() => {
            void onDuplicateMeasurement(measurement._id);
            onClose();
          }}
        >
          Duplicate
        </ViewportContextMenuItem>
      ) : null}
      {measurement && onDuplicateMeasurements && contextSelectionIds.length > 1 ? (
        <ViewportContextMenuItem
          onSelect={() => {
            void onDuplicateMeasurements(contextSelectionIds);
            onClose();
          }}
        >
          Duplicate Selected
        </ViewportContextMenuItem>
      ) : null}
      {measurement && onDeleteMeasurement ? (
        <ViewportContextMenuItem
          destructive
          icon={<Trash2 className="size-3.5" />}
          onSelect={() => {
            void onDeleteMeasurement(measurement._id);
            onClose();
          }}
        >
          Delete
        </ViewportContextMenuItem>
      ) : null}
      {measurement && onDeleteMeasurements && contextSelectionIds.length > 1 ? (
        <ViewportContextMenuItem
          destructive
          icon={<Trash2 className="size-3.5" />}
          onSelect={() => {
            void onDeleteMeasurements(contextSelectionIds);
            onClose();
          }}
        >
          Delete Selected
        </ViewportContextMenuItem>
      ) : null}
      <MilestonesContextMenuItem
        measurement={measurement}
        contextSelectionCount={contextSelectionIds.length}
        onOpenMilestones={onOpenMilestones}
        onClose={onClose}
      />
      {measurement && onApplyMeasurementLeaders ? (
        <ViewportContextMenuItem
          onSelect={async () => {
            onClose();
            try {
              const applied = await onApplyMeasurementLeaders(
                contextSelectionIds,
                nextLeaderVisible
              );
              if (!applied) {
                toast.error("Failed to update measurement leaders");
                return;
              }
              toast.success(
                contextSelectionIds.length > 1
                  ? `${contextSelectionIds.length} measurements updated`
                  : "Measurement updated"
              );
            } catch (error) {
              console.error(error);
              notifyError(error, "Failed to update measurement leaders");
            }
          }}
        >
          {leaderActionLabel}
        </ViewportContextMenuItem>
      ) : null}
      <LabelGroupContextMenuItems
        canGroupSelection={canGroupSelection}
        canUngroupSelection={canUngroupSelection}
        selectionIds={contextSelectionIds}
        onGroupLabels={onGroupLabels}
        onUngroupLabels={onUngroupLabels}
        onClose={onClose}
      />
      {canInsertPoint ? (
        <ViewportContextMenuItem
          onSelect={async () => {
            if (!pagePoint || !measurement) {
              return;
            }
            try {
              await onInsertPoint(measurement._id, pagePoint);
              onClose();
            } catch (error) {
              notifyError(error, getUserFacingErrorMessage(error, "Failed to insert point"));
            }
          }}
        >
          Insert Point
        </ViewportContextMenuItem>
      ) : null}
      {canRemovePoint ? (
        <ViewportContextMenuItem
          destructive
          onSelect={async () => {
            if (vertexIndex === undefined || !measurement) {
              return;
            }
            try {
              await onRemovePoint(measurement._id, vertexIndex);
              onClose();
            } catch (error) {
              notifyError(error, getUserFacingErrorMessage(error, "Failed to remove point"));
            }
          }}
        >
          Remove Point
        </ViewportContextMenuItem>
      ) : null}
      {canPasteMeasurement && onPasteMeasurement ? (
        <ViewportContextMenuItem
          onSelect={() => {
            void onPasteMeasurement();
            onClose();
          }}
        >
          Paste
        </ViewportContextMenuItem>
      ) : null}
    </ViewportContextMenu>
  );
}
